import { Component, OnInit } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material";
import { ActivatedRoute, Router } from "@angular/router";
import { formatNumber } from "@angular/common";
import { isArray } from "lodash";

import { ConfigService } from "../../../../../service/config.service";
import { UserAttributeService } from "../../../../../service/finzy-flex/user/user-attribute.service";
import { ProductCode, ProductDataForSwift, ProductDetailsForExpansionTab, ProductList } from "../../../../../model/finzy-flex/product-list.model";
import { LenderBasicInfo } from "../../../../../model/lender.model";
import { AddFundPopupComponent } from "../../add-fund-popup/add-fund-popup.component";
import { FinzyBoltPopupComponent } from "../../finzy-bolt-popup/finzy-bolt-popup.component";
import { Keys, StorageService } from "../../../../../service/storage.service";
import { SharedPreferenceDataService } from "../../../../../service/shared-preference-data.service";
import { Toaster, Type } from "../../../../../toaster/Toaster";
import { AttributeCode, AttributeValue, InvestmentPreference, UserAttributeInterface } from "../../../../../model/finzy-flex/user-attribute.model";
import { FfEndPoints } from "../../ff-endpoints";
import { LenderAccountInfo } from "../../../../../model/finzy-flex/lender-account.model";
import { AccountService } from "../../../../../service/finzy-flex/account/account.service";
import { NCashData, NCashDetail } from "../../../../../model/finzy-flex/nCash.model";
import { NCashDataUtil } from "../../../../../utils/finzy-flex/nCash-data.util";
import { Logger } from "../../../../../utils/Logger";
import { ProductService } from "../../../../../service/finzy-flex/product/product.service";
import { WithdrawalService } from "../../../../../service/withdrawal.service";
import { WithdrawalRequestStatus } from "../../../../../model/finzy-flex/withdraw-money.model";
import { LenderProductWisePo } from "../../../../../model/LenderProductWisePo.mode";

@Component({
    selector: 'app-flex-dashboard',
    templateUrl: './flex-dashboard.component.html',
    styleUrls: ['./flex-dashboard.component.css']
})
export class FlexDashboardComponent implements OnInit {
    private readonly configService: ConfigService;
    private readonly storageService: StorageService;
    private readonly sharedPreferenceDataService: SharedPreferenceDataService;
    private readonly userAttributeService: UserAttributeService;
    private readonly accountService: AccountService;
    private readonly productService: ProductService;
    private readonly withdrawalService: WithdrawalService;
    private readonly matDialog: MatDialog;
    private finzyBoltStatus: boolean = true;
    private router: Router;
    public productList: ProductList = null;
    private nCashDataUtil: NCashDataUtil;

    public expandRoi: boolean = false;
    public expandPrincipalOutstanding: boolean = false;
    public expandInterestEarned: boolean = false;
    public roiDetails: ProductDetailsForExpansionTab[] = [];
    public principalOutStandingDetails: ProductDetailsForExpansionTab[] = [];
    public interestEarnedDetails: ProductDetailsForExpansionTab[] = [];
    public lenderBasicInfo: LenderBasicInfo;
    public lenderAccountInfo: LenderAccountInfo = null;
    public lenderAccountInfoFromLE: {lenderPO: number, lenderIE: number} = {lenderPO: null, lenderIE: null};
    public isSwiftOn: boolean = false;
    public isNCashOn: boolean = false;
    public preferences: UserAttributeInterface | null;
    public nCashDate: any = null;
    public nCashData: NCashDetail;
    public nCashAmountToShow = null;
    public nCashType: 'maxNCash' | 'interestOnly' | 'custom' = null;
    public investmentOptions: ProductDataForSwift[];

    public isWithdrawalRequestNotCompleted: boolean = true;
    public setTimeOutStatus: string = 'start';
    public withdrawalButtonHelperText = null;
    
    constructor(
        router: Router,
        configService: ConfigService,
        storageService: StorageService,
        sharedPreferenceDataService: SharedPreferenceDataService,
        userAttributeService: UserAttributeService,
        accountService: AccountService,
        matDialog: MatDialog,
        activatedRoute: ActivatedRoute,
        nCashDataUtil: NCashDataUtil,
        productService: ProductService,
        withdrawalService: WithdrawalService
    ) {
        this.router = router;
        this.configService = configService;
        this.storageService = storageService;
        this.sharedPreferenceDataService = sharedPreferenceDataService;
        this.userAttributeService = userAttributeService;
        this.accountService = accountService;
        this.productService = productService;
        this.withdrawalService = withdrawalService;
        this.matDialog = matDialog;
        this.nCashDataUtil = nCashDataUtil;
    }

    async ngOnInit() {
        this.getWithdrawalRequestsListByLenderId();
        await this.getLenderProductWisePo();
        await this.getLenderAccountInfo();
        await this.getProductsList();
        await this.getLenderPreferences();
        this.getWithdrawalRequestsListByLenderId();
    }
    
    public async getLenderPreferences() {
        const profileId = this.storageService.getItem(Keys.PROFILE_ID);
        try {
            const userAttribute = await this.userAttributeService.getUserAttributesById(profileId);
            this.preferences = userAttribute ?? null;

            this.formattingData();
            this.changeFinzyBoltStatus();
            this.readPreferenceSharedData();
            this.getWalletAmount();
            if (this.preferences.data[0]?.investmentPreferences.some(attr => attr.attributeCode === AttributeCode.NCASH_WD_LIMIT)) {
                await this.getNCashDetails();
            } else {
                this.nCashAmountToShow = 0
            }
            this.storageService.removeItem(Keys.PARENT_WALLET_PATH);
        } catch(e) {
            this.preferences = null;
        }
        
    }

    public async getLenderProductWisePo() {
        const lenderId = this.storageService.getItem(Keys.LENDER_ID);
        try {
            const res: any = await this.accountService.getLenderProductWisePo(lenderId);
            const lenderProductWisePos: LenderProductWisePo[] =  res.data ?? null;
            if(lenderProductWisePos.length) {
                this.lenderAccountInfoFromLE.lenderPO = lenderProductWisePos.reduce((initialVal, currentVal) => initialVal + currentVal.productPrincipalOutstanding, 0);
                this.lenderAccountInfoFromLE.lenderIE = lenderProductWisePos.reduce((initialVal, currentVal) => initialVal + currentVal.productInterestAmount, 0);
            } else {
                this.lenderAccountInfoFromLE = { lenderPO: 0, lenderIE: 0 };
            }
        } catch(e) {
            console.log(e)
            this.lenderAccountInfoFromLE = { lenderPO: 0, lenderIE: 0 };
        } 
    }
 
    public async getLenderAccountInfo() {
        const lenderId = this.storageService.getItem(Keys.LENDER_ID);
        try {
            const lenderAccountInfo: any = await this.accountService.getLenderAccountInfo(lenderId);
            this.lenderAccountInfo = lenderAccountInfo.data[0] ?? null;
            this.initializeLenderAccountInfo(this.lenderAccountInfo);
        } catch(e) {
            this.lenderAccountInfo =  null;
        } 
    }

    private initializeLenderAccountInfo(lenderAccountInfo: LenderAccountInfo) {
        if (lenderAccountInfo) {
          return;
        } else {
          this.lenderAccountInfo = {
            lenderId: null,
            principalOutstanding: 0.0,
            interestAmount: 0.0,
            withdrawalAmount: 0.0,
            withdrawBackMoney: 0.0,
            roi: 0.001,
            maxNCashAmount: 0.0,
            nCashAmount: 0.0,
            nCashDate: null,
            products: []
          };
        }
    }
 
    public async getProductsList() {
        const lenderId = this.storageService.getItem(Keys.LENDER_ID);
        try {
            const applicableProducts = await this.productService.getSubProductsList('lender', lenderId);
            this.productList = applicableProducts ?? null;
        } catch(e) {
            this.productList = null;
        }
    }

    public formattingData() {
        const lenderProductList: string[] = [];
        const filteredSubProductList = this.productList.data.filter(product => (new Date(product.productVersions[0].startDate).getTime() <= new Date().getTime() &&
            new Date().getTime() <= new Date(product.productVersions[0].endDate).getTime()));
        this.lenderAccountInfo?.products.forEach((element) => {
            lenderProductList.push(element.productCode);
            filteredSubProductList?.forEach((product) => {
                if (product?.code == element.productCode) {
                    const productName = product?.productVersions[0].productAttributes.find((attribute) => attribute.code == ProductCode.NAME) ? product?.productVersions[0].productAttributes.find((attribute) => attribute.code == ProductCode.NAME).value.toString() : product.name.toString();
                    this.isWithdrawalAmountIsEqualToInitialPO(element.investmentAmount, element.interestAmount, element.withdrawalAmount, element.nCashAmount, element.creditLossAmount) ?
                    this.populatingDataInArray(productName, 0, 0, 0) :
                    this.populatingDataInArray(productName, element.roi, element.principalOutstanding, element.interestAmount);
                }
            });
        });
        this.investmentOptions = [];
        filteredSubProductList.forEach((product) => {
            if (!lenderProductList.includes(product?.code)) {
                const productName = product?.productVersions[0].productAttributes.find((attribute) => attribute.code == ProductCode.NAME) ? product?.productVersions[0].productAttributes.find((attribute) => attribute.code == ProductCode.NAME).value.toString() : product.name.toString();
                this.populatingDataInArray(productName, 0, 0, 0);
            }
            const investmentOption: ProductDataForSwift = {
                name: product?.productVersions[0].productAttributes.find((attribute) => attribute.code == ProductCode.NAME) ? product?.productVersions[0].productAttributes.find((attribute) => attribute.code == ProductCode.NAME)?.value.toString() : product.name.toString(),
                mph: product?.productVersions[0].productAttributes.find((attribute) => attribute.code == ProductCode.MHP_FRONTEND_DISPLAY)?.value.toString() ?? '0',
                interest: product?.productVersions[0].productAttributes.find((attribute) => attribute.code == ProductCode.ROI)?.value.toString() ?? '0',
                percentage: '',
                maturity_days: product.productVersions[0].productAttributes.find(attribute => attribute.code === ProductCode.MATURITY_DAYS)?.value,
            };
            this.investmentOptions.push(investmentOption);
        });
    }

    private isWithdrawalAmountIsEqualToInitialPO(productInvestmentAmount: number, productInterestAmount: number, totalWithdrawalAmount: number, nCashAmount: number, creditLossAmount: number): boolean {
        const initialPO = productInvestmentAmount + productInterestAmount;
        const totalDeduction = totalWithdrawalAmount + nCashAmount + creditLossAmount;
        return (initialPO - totalDeduction) < 0.01;
    }

    private populatingDataInArray(productCode: string, roi: number, principalOutstanding: number, interestAmount: number) {
        const roiDetail: ProductDetailsForExpansionTab = {
            name: productCode,
            value: roi ? `${formatNumber(roi, "en-IN", "1.0-2")} %` : 0,
        };
        const principalOutStandingDetail: ProductDetailsForExpansionTab = {
            name: productCode,
            value: formatNumber(this.preciseNumber(principalOutstanding) ?? 0, "en-IN", "1.0-2"),
        }
        const interestEarnedDetail: ProductDetailsForExpansionTab = {
            name: productCode,
            value: formatNumber(this.preciseNumber(interestAmount) ?? 0, "en-IN", "1.0-2"),
        }
        this.roiDetails.push(roiDetail);
        this.principalOutStandingDetails.push(principalOutStandingDetail);
        this.interestEarnedDetails.push(interestEarnedDetail);
    }

    private async changeFinzyBoltStatus() {
        (this.preferences.data?.length > 0) ? this.preferences.data.forEach((preference) => {
            this.finzyBoltStatus = preference?.automaticInvestmentsFromWallet;
        }) : this.finzyBoltStatus = false;
    }

    private async readPreferenceSharedData() {
        await this.sharedPreferenceDataService.swiftStatus$?.subscribe((data) => {
            this.isSwiftOn = data;
        });
        await this.sharedPreferenceDataService.nCashStatus$?.subscribe((data) => {
            this.isNCashOn = data;
        });
    }

    private openFinzyBoltPopUp(addMoney: boolean, boltEnabled: boolean, redirectionPath?: string) {
        const matDialogConfig: MatDialogConfig = {
            height: 'auto',
            width: '60%',
            panelClass: 'finzy-bolt-popup',
            data: {
                addMoney: addMoney,
                boltEnabled: boltEnabled,
            }
        };
        this.matDialog.open(FinzyBoltPopupComponent, matDialogConfig)
            .afterClosed().subscribe(
                async (result) => {
                    if (result == false) {
                        await this.preferences.data.forEach((preference) => {
                            preference.automaticInvestmentsFromWallet = result;
                        });
                        const preferenceDetails = await this.userAttributeService.putUserAttribute({ investmentPreferences: this.preferences.data[0].investmentPreferences, automaticInvestmentsFromWallet: false, isNCashModified: false, isSwiftModified: false, userId: this.preferences.data[0]?.userId }, this.storageService.getItem(Keys.PROFILE_ID))
                            .catch((error) => {
                                return Toaster.toast(`Something went wrong.`, Type.Error);
                            });
                        this.preferences = (preferenceDetails) ? preferenceDetails : null;
                        if (this.preferences.data[0].automaticInvestmentsFromWallet == false) {
                            (redirectionPath == FfEndPoints.PORTFOLIO_CREATION) ? this.router.navigate([`${redirectionPath}`]) : window.location.reload();
                        }
                    }
                }
            );
    }

    public onExpand(expandableItem: string) {
        this.expandRoi = false;
        this.expandPrincipalOutstanding = false;
        this.expandInterestEarned = false;

        if(expandableItem == 'expandRoi') {
            this.expandRoi = true
        } else if(expandableItem == 'expandPrincipalOutstanding') {
            this.expandPrincipalOutstanding = true
        } else if(expandableItem == 'expandInterestEarned') {
            this.expandInterestEarned = true
        }
    }

    public onCollapse(expandableItem: string) {
        (expandableItem == 'expandRoi') && (this.expandRoi = false);
        (expandableItem == 'expandPrincipalOutstanding') && (this.expandPrincipalOutstanding = false);
        (expandableItem == 'expandInterestEarned') && (this.expandInterestEarned = false);
    }

    public navigateToPreferences() {
        return this.router.navigate([FfEndPoints.PREFERENCES]);
    }

    public onAddFunds() {
        this.storageService.saveItem(Keys.PARENT_WALLET_PATH, this.router.url);
        if (this.finzyBoltStatus) {
            this.openFinzyBoltPopUp(true, this.finzyBoltStatus);
        } else {
            const initialFundAllocationMinAmt = this.configService.getInitialFundAllocationMinAmount();
            const postFundAllocationMinAmount = this.configService.getFundAllocationMinAmount();
            const fundAllocationMinAmount = this.lenderBasicInfo?.portfolioPresent ? postFundAllocationMinAmount : initialFundAllocationMinAmt;
            const matDialogConfig: MatDialogConfig = {
                height: '90%',
                width: '80%',
                panelClass: "dialog-responsive",
                data: { virtualAccountNumber: this.lenderBasicInfo?.virtualAccountNumber, fundAllocationMinimumAmount: fundAllocationMinAmount }
            };
            this.matDialog.open(AddFundPopupComponent, matDialogConfig).afterClosed().subscribe(response => {
                if (!response)
                    this.storageService.removeItem(Keys.PARENT_WALLET_PATH);
            })
        }
    }

    onWithdrawFunds() {
        this.router.navigate([FfEndPoints.INITIATE_WITHDRAWAL]);
    }

    public onClickInvestNow() {
        if (this.finzyBoltStatus) {
            this.openFinzyBoltPopUp(false, this.finzyBoltStatus, FfEndPoints.PORTFOLIO);
        } else {
            return this.router.navigate([FfEndPoints.PORTFOLIO]);
        }
    }

    public async getWalletAmount() {
        const lenderId: string = this.storageService.getItem('lender_id');
        this.lenderBasicInfo = await this.userAttributeService.getLenderBasicInfo(lenderId);
    }

    private async getNCashDetails() {
        const investmentPreferences = this.preferences.data[0].investmentPreferences;
        const nCashType = investmentPreferences.find(attr => attr.attributeCode === AttributeCode.NCASH_WD_LIMIT).attributeValue;
        const nCashFrequency = investmentPreferences.find(attr => attr.attributeCode === AttributeCode.NCASH_WD_FREQUENCY).attributeValue;
        const nCashDateSelection = investmentPreferences.find(attr => attr.attributeCode === AttributeCode.NCASH_WD_DATE_OF_MONTH).attributeValue;
        await this.nCashDataUtil.getNCashDetailsByLenderId(nCashType, nCashFrequency, nCashDateSelection)
            .then((nCash: NCashData) => {
                if (nCash.status == 0) {
                    this.nCashAmountToShow = nCashType === AttributeValue.CUSTOM ?
                        this.getCustomNCashAmount(investmentPreferences) : nCash.data[0].ncashAmount;
                    const date = nCash.data[0]?.ncashPayOutDisplay;
                    this.nCashDate = date ? date : '--';
                } else {
                    Toaster.toast('Something went wrong. Please try again later.', Type.Error);
                }
            }).catch(() => { Logger.log('Not able to fetch nCash details') });
    }

    private getCustomNCashAmount(investmentPreferences: InvestmentPreference[]) {
        const nCashPreferenceAmount = investmentPreferences.find(attr => attr.attributeCode === AttributeCode.NCASH_WD_AMOUNT).attributeValue;
        if(this.lenderAccountInfo?.principalOutstanding) {
            return nCashPreferenceAmount > this.lenderAccountInfo.principalOutstanding ? this.lenderAccountInfo.principalOutstanding : nCashPreferenceAmount;
        } else {
            return 0.0;
        }
    }

    public preciseNumber(number: number) {
        return Math.floor(number * 100) / 100;
    }  
    
    public getWithdrawalRequestsListByLenderId() {
        const lenderId: string = this.storageService.getItem('lender_id');
        this.withdrawalService.getWithdrawalRequestsListByLenderId(lenderId)
            .then((res) => {
                if(isArray(res) && res.length) {
                    const withdrawalRequestWithNewAndInProgress= res.find((val) => { 
                        const status: any = val.status;
                        return ([WithdrawalRequestStatus.IN_PROGRESS, WithdrawalRequestStatus.NEW, WithdrawalRequestStatus.FAILED].includes(status));
                    });
                    if(withdrawalRequestWithNewAndInProgress) {
                        this.isWithdrawalRequestNotCompleted = true;
                        this.withdrawalButtonHelperText = 'Please wait, your previous withdrawal request is still in process';
                        this.checkingWithdrawalRequestStatus();
                    } else {
                        this.isWithdrawalRequestNotCompleted = false;
                        this.withdrawalButtonHelperText = null;
                    }
                } else {
                    this.isWithdrawalRequestNotCompleted = false;
                }
            })
            .catch((error) => {
                console.log(error)
            })
    }

    private checkingWithdrawalRequestStatus() {
        if(this.setTimeOutStatus === 'start') {
            setTimeout(() => {
                this.setTimeOutStatus = 'stop';
                this.getWithdrawalRequestsListByLenderId();
            },15000)
        }
    }

    public isDisableWithdrawalNowButton(): boolean {
        console.log(this.lenderAccountInfo)
        if(!this.lenderAccountInfo) {
            return true;
        } else if(this.lenderAccountInfo?.withdrawBackMoney <= 0) {
            return true;
        } else if(this.isWithdrawalRequestNotCompleted) {
            return true;
        }
        return false;
    }
}